import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/scandinavianAirlines/Sidebar";
import ResultsHeader from "../../components/scandinavianAirlines/ResultsHeader";
import LoadingSpinner from "../../components/scandinavianAirlines/LoadingSpinner";
import { MovmoCheckoutButton, consumeMovmoCheckoutReturn } from "@movmo/express-checkout-button";
import { buildMovmoFlightOfferForPa } from "../../services/mockOffers";
import { queryWithoutMovmo } from "../../utils/preserveQuery";

const TravelerDetailsView: React.FC = () => {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [flightData, setFlightData] = useState<any>(null);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);

    // MOVMO-549: Handle page-level return from top-level redirect.
    useEffect(() => {
        consumeMovmoCheckoutReturn({
            isProd: false,
            onBookingComplete: () => {
                console.log("[sas-travelers] booking complete");
            },
            onExit: ({ reason }: { reason: string }) => {
                console.log("[sas-travelers] movmo checkout exit:", reason);
                if (reason === "booking_confirmed") {
                    setBookingConfirmed(true);
                } else if (reason !== undefined) {
                    navigate("/scandinavian-airlines" + queryWithoutMovmo(window.location.search));
                }
            },
        });
    }, []);

    useEffect(() => {
        // Load selected flight from sessionStorage
        const storedData = sessionStorage.getItem("selectedSASFlightOffer");
        if (storedData) {
            try {
                const parsedData = JSON.parse(storedData);
                setFlightData(parsedData);
            } catch (error) {
                console.error("Error parsing stored flight data:", error);
                navigate("/scandinavian-airlines", { replace: true });
            }
        } else if (!bookingConfirmed) {
            // No selection found, redirect to search (unless we're showing confirmation)
            navigate("/scandinavian-airlines", { replace: true });
        }
        setIsLoading(false);
    }, [navigate, bookingConfirmed]);

    const formatPriceWithSpace = (amount: number) => {
        const [whole, decimal] = amount.toFixed(2).split(".");
        const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return `${formattedWhole},${decimal}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date
            .toLocaleDateString("en-US", { month: "short" })
            .toUpperCase();
        return `${day} ${month}`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    if (!flightData) {
        if (!bookingConfirmed) {
            return null;
        }
        // Render minimal confirmation page
        return (
            <div className="min-h-screen bg-gray-50">
                <ResultsHeader />
                <div className="flex items-center justify-center py-16 px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
                        <div className="text-2xl font-semibold text-green-600 mb-2">
                            Booking confirmed with Movmo
                        </div>
                        <p className="text-gray-600 mb-6">Your booking has been successfully confirmed.</p>
                        <button
                            onClick={() => navigate("/scandinavian-airlines")}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            Return to Search
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalPassengers =
        flightData.passengers.adults +
        flightData.passengers.children +
        flightData.passengers.infants;

    // Create array of passenger types for rendering
    const passengers: Array<{
        type: "adult" | "child" | "infant";
        index: number;
    }> = [];
    for (let i = 0; i < flightData.passengers.adults; i++) {
        passengers.push({ type: "adult", index: passengers.length + 1 });
    }
    for (let i = 0; i < flightData.passengers.children; i++) {
        passengers.push({ type: "child", index: passengers.length + 1 });
    }
    for (let i = 0; i < flightData.passengers.infants; i++) {
        passengers.push({ type: "infant", index: passengers.length + 1 });
    }

    return (
        <div className="min-h-screen bg-[#f5f5f5] pb-24">
            {/* Sidebar */}
            <Sidebar
                activeItem="Book"
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
            />

            {/* Main Content */}
            <div className="min-h-screen">
                {/* Header */}
                <ResultsHeader currentStep={2} />

                {/* Main Content Area */}
                <div className="max-w-[1120px] mx-auto px-8 py-14">
                    <div className="-mr-14">
                        <h1 className="text-3xl font-semibold text-[#383838] mb-3 text-center">
                            Add traveler details
                        </h1>
                        <p className="text-sm text-[#383838] mb-8 text-center">
                            Fill in your traveler information below. Remember to
                            write your name as it's shown in your passport.
                        </p>
                    </div>

                    {/* Login Section */}
                    <div className="bg-[#e2f0f7] border border-[#e2f0f7] rounded-lg py-2 px-4 mb-4 -mr-14">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold mb-2 text-[#383838]">
                                    Log in for a smoother experience
                                </h2>
                                <p className="text-sm text-[#383838]">
                                    Pre-fill your personal details, earn points
                                    on flights and enjoy your membership
                                    benefits.
                                </p>
                            </div>
                            <div>
                                <button className="px-6 py-2 bg-white text-[#0103bd] border-2 border-[#0103bd] rounded-full font-semibold hover:bg-gray-50 transition-colors">
                                    LOG IN
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Movmo Section */}
                    <div className="bg-brand-100 border border-brand-100 rounded-lg py-2 px-4 mb-4 -mr-14">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold mb-2 text-[#383838]">
                                    Skip the forms. Fly faster.
                                </h2>
                                <p className="text-sm text-[#383838]">
                                    Store your details once. Checkout fast
                                    across airlines, earn loyalty points, and
                                    book stress-free.
                                </p>
                            </div>
                            <div className="movmo-button-wrapper">
                                <style>{`
                                    .movmo-button-wrapper button {
                                        padding: 0.5rem 1.5rem !important;
                                        border-radius: 9999px !important;
                                        font-weight: 600 !important;
                                        font-size: 16px !important;
                                        line-height: 20px !important;
                                        border: none !important;
                                    }
                                `}</style>
                                <MovmoCheckoutButton
                                    iataCode="SK"
                                    apiKey={
                                        process.env.REACT_APP_MOVMO_API_KEY ||
                                        ""
                                    }
                                    // The Amadeus provider was retired
                                    // (MOVMO-590); the SAS demo hands the
                                    // canonical Movmo offer to the mock-pa
                                    // adapter like the PA demo does.
                                    providerID="mock-pa"
                                    flightOffer={
                                        flightData
                                            ? buildMovmoFlightOfferForPa(
                                                  flightData.flight,
                                                  {
                                                      adults:
                                                          flightData.passengers
                                                              ?.adults ?? 1,
                                                      children:
                                                          flightData.passengers
                                                              ?.children ?? 0,
                                                      infants:
                                                          flightData.passengers
                                                              ?.infants ?? 0,
                                                  },
                                                  {
                                                      id: "mock-carrier-sk",
                                                      name: "Scandinavian Airlines",
                                                      iata_code: "SK",
                                                  },
                                              )
                                            : undefined
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="text-sm mb-2 text-[#383838] text-right -mr-14">
                        * Required information
                    </div>

                    {/* Traveler Forms */}
                    {passengers.map((passenger, idx) => (
                        <div
                            key={idx}
                            className="rounded-lg overflow-hidden mb-6 -mr-14"
                        >
                            {/* Header */}
                            <div className="bg-[#d9d9d9] px-6 h-[68px] rounded-lg flex items-center relative z-10">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-md font-bold text-[#383838] uppercase">
                                        TRAVELER {passenger.index}
                                    </h3>
                                    <span className="text-sm text-[#383838]">
                                        ({passenger.type})
                                    </span>
                                    {passenger.index === 1 && (
                                        <span className="text-sm text-[#383838] ml-4">
                                            This traveler will receive the
                                            receipt for the trip
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="bg-[#eae9e8] p-2 -mt-2 rounded-b-lg relative">
                                {/* White Card for Input Fields */}
                                <div className="bg-white rounded-lg p-4 mb-2">
                                    <p className="text-sm text-[#383838] mb-4">
                                        Please enter the name as shown in the
                                        passport
                                    </p>

                                    {passenger.index === 1 ? (
                                        <>
                                            {/* First Traveler: Name and Gender Row */}
                                            <div className="grid grid-cols-4 gap-2 mb-2">
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        First name*
                                                    </label>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        Middle name(s)
                                                    </label>
                                                    <span className="absolute right-3 top-4 text-blue-600 text-sm">
                                                        ⓘ
                                                    </span>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        Last name*
                                                    </label>
                                                </div>
                                                <div className="relative">
                                                    <select
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#6b6b6b] appearance-none cursor-pointer"
                                                        disabled
                                                    >
                                                        <option value="">
                                                            Gender*
                                                        </option>
                                                        <option value="male">
                                                            Male
                                                        </option>
                                                        <option value="female">
                                                            Female
                                                        </option>
                                                    </select>
                                                    <svg
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>

                                            {/* Date of Birth, Email, Phone Row */}
                                            <div className="grid grid-cols-4 gap-2 mb-2">
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        Date of birth
                                                        (YYYY-MM-DD) *
                                                    </label>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="email"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        Email*
                                                    </label>
                                                </div>
                                                <div className="relative">
                                                    <input
                                                        type="tel"
                                                        className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                        placeholder=" "
                                                        disabled
                                                    />
                                                    <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                        Phone*
                                                    </label>
                                                </div>
                                            </div>

                                            {/* EuroBonus Checkbox - only for first traveler */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={`eurobonus${passenger.index}`}
                                                    className="w-4 h-4 rounded border-gray-400"
                                                    disabled
                                                />
                                                <label
                                                    htmlFor={`eurobonus${passenger.index}`}
                                                    className="text-sm text-[#383838]"
                                                >
                                                    Join EuroBonus and earn
                                                    points
                                                </label>
                                                <span className="text-blue-600 text-sm">
                                                    ⓘ
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        /* Other Travelers: 5 column layout */
                                        <div className="grid grid-cols-5 gap-2">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                    placeholder=" "
                                                    disabled
                                                />
                                                <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                    First name*
                                                </label>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                    placeholder=" "
                                                    disabled
                                                />
                                                <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                    Middle name(s)
                                                </label>
                                                <span className="absolute right-3 top-4 text-blue-600 text-sm">
                                                    ⓘ
                                                </span>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                    placeholder=" "
                                                    disabled
                                                />
                                                <label className="absolute left-4 top-4 text-sm text-[#6b6b6b] pointer-events-none">
                                                    Last name*
                                                </label>
                                            </div>
                                            <div className="relative">
                                                <select
                                                    className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#6b6b6b] appearance-none cursor-pointer"
                                                    disabled
                                                >
                                                    <option value="">
                                                        Gender*
                                                    </option>
                                                    <option value="male">
                                                        Male
                                                    </option>
                                                    <option value="female">
                                                        Female
                                                    </option>
                                                </select>
                                                <svg
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    className="w-full h-[56px] px-4 pt-4 pb-2 border border-gray-400 rounded-md bg-white text-[#383838]"
                                                    placeholder=" "
                                                    disabled
                                                />
                                                <label className="absolute left-4 top-4 text-xs text-[#6b6b6b] pointer-events-none">
                                                    Date of birth (YYYY-MM-DD) *
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Frequent Flyer and Assistance Card */}
                                <div className="bg-white rounded-lg px-6 py-4">
                                    <div className="space-y-3">
                                        <a
                                            href="#"
                                            className="text-[#0033cc] text-sm underline block"
                                        >
                                            Add frequent flyer program
                                        </a>
                                        <a
                                            href="#"
                                            className="text-[#0033cc] text-sm underline flex items-center gap-2"
                                        >
                                            <span className="text-base">
                                                ♿
                                            </span>
                                            Request assistance
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom Summary Bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
                    <div className="max-w-[1120px] mx-auto px-8 relative">
                        {/* View trip button */}
                        <div className="absolute left-[53%] transform -translate-x-1/2 -top-6">
                            <button className="bg-white px-4 py-1 rounded-md shadow-t-md text-sas-blue text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors">
                                View trip
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="py-4 flex justify-between items-center">
                            {/* Left Column - Trip Summary */}
                            <div className="flex items-start gap-12">
                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        {flightData.tripType === "RT"
                                            ? "Round Trip"
                                            : "One Way"}
                                    </div>
                                    <div className="text-sm text-[#383838] leading-tight">
                                        {flightData.origin} –{" "}
                                        {flightData.destination}
                                        {flightData.tripType === "RT" && (
                                            <>
                                                <br />
                                                {flightData.destination} –{" "}
                                                {flightData.origin}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        Dates
                                    </div>
                                    <div className="text-sm text-[#383838]">
                                        {flightData.tripType === "RT" ? (
                                            <>
                                                {formatDate(
                                                    flightData.departureDate
                                                )}{" "}
                                                –{" "}
                                                {formatDate(
                                                    flightData.returnDate ||
                                                        flightData.departureDate
                                                )}
                                            </>
                                        ) : (
                                            formatDate(flightData.departureDate)
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-between h-14">
                                    <div className="text-sm text-[#383838] uppercase font-bold">
                                        Travelers
                                    </div>
                                    <div className="text-sm text-[#383838]">
                                        {totalPassengers}{" "}
                                        {totalPassengers === 1
                                            ? "adult"
                                            : "adults"}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Price and Actions */}
                            <div className="flex items-center gap-6 -mr-16">
                                <div className="flex items-center gap-3">
                                    <svg
                                        className="w-5 h-5 text-[#3f3d3a]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                        />
                                    </svg>
                                    <div>
                                        <div className="text-sm text-[#383838] uppercase font-bold">
                                            Total
                                        </div>
                                        <div className="text-md font-bold text-[#383838]">
                                            {formatPriceWithSpace(
                                                flightData.price
                                            )}{" "}
                                            EUR
                                        </div>
                                    </div>
                                </div>

                                <div className="text-xs">
                                    <a
                                        href="#"
                                        className="text-blue-600 underline"
                                    >
                                        incl.{" "}
                                        {formatPriceWithSpace(flightData.taxes)}{" "}
                                        EUR
                                        <br />
                                        Taxes & carrier-imposed fees
                                    </a>
                                </div>

                                <button className="px-8 py-2.5 bg-[#0103bd] text-white rounded-full text-sm font-bold hover:bg-[#0102a0] uppercase tracking-wider transition-colors">
                                    CONTINUE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TravelerDetailsView;
